import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  CalendarDays,
  FolderKanban,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Moon,
  NotebookPen,
  Plus,
  Search,
  Timer,
  User as UserIcon,
} from 'lucide-react';
import type { SearchHit, SearchType } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useUiStore } from '@/stores/ui';

interface SearchResponse {
  exact?: SearchHit;
  groups: { type: SearchType; hits: SearchHit[] }[];
}

interface CommandAction {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
}

const TYPE_LABEL: Record<SearchType, string> = {
  task: 'Tasks',
  project: 'Projects',
  note: 'Notes',
  comment: 'Comments',
  user: 'People',
};

/** `⌘K` palette: fuzzy search, `>` command mode, recents, exact-key jump (§F12). */
export function CommandPalette() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setQuickAdd = useUiStore((s) => s.setQuickAddOpen);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const [query, setQuery] = useState('');
  const isCommandMode = query.startsWith('>');
  const term = isCommandMode ? query.slice(1).trim() : query.trim();

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const { data: recents } = useQuery({
    queryKey: ['search-recent'],
    queryFn: async () => (await api.get<SearchHit[]>('/search/recent')).data,
    enabled: open,
    staleTime: 60_000,
  });

  const { data: results } = useQuery({
    queryKey: ['search', term],
    queryFn: async () => (await api.get<SearchResponse>(`/search${qs({ q: term })}`)).data,
    enabled: open && !isCommandMode && term.length >= 2,
  });

  const commands: CommandAction[] = useMemo(
    () => [
      {
        id: 'new-task',
        label: 'Create task',
        hint: '⌘N',
        icon: <Plus className="size-4" />,
        run: () => setQuickAdd(true),
      },
      {
        id: 'my-day',
        label: 'Go to My Day',
        hint: 'G then D',
        icon: <ListTodo className="size-4" />,
        run: () => navigate('/my-day'),
      },
      {
        id: 'inbox',
        label: 'Go to Inbox',
        hint: 'G then I',
        icon: <NotebookPen className="size-4" />,
        run: () => navigate('/inbox'),
      },
      {
        id: 'projects',
        label: 'Go to Projects',
        hint: 'G then P',
        icon: <FolderKanban className="size-4" />,
        run: () => navigate('/projects'),
      },
      {
        id: 'notes',
        label: 'Go to Notes',
        icon: <NotebookPen className="size-4" />,
        run: () => navigate('/notes'),
      },
      {
        id: 'timesheet',
        label: 'Go to Timesheet',
        icon: <Timer className="size-4" />,
        run: () => navigate('/timesheet'),
      },
      {
        id: 'leave',
        label: 'Go to Leave',
        icon: <CalendarDays className="size-4" />,
        run: () => navigate('/leave'),
      },
      {
        id: 'dashboard',
        label: 'Go to Dashboard',
        icon: <LayoutDashboard className="size-4" />,
        run: () => navigate('/dashboard'),
      },
      {
        id: 'profile',
        label: 'Go to Settings',
        icon: <UserIcon className="size-4" />,
        run: () => navigate('/settings/profile'),
      },
      {
        id: 'theme',
        label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`,
        hint: '⌘⇧D',
        icon: <Moon className="size-4" />,
        run: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
      },
      {
        id: 'logout',
        label: 'Log out',
        icon: <LogOut className="size-4" />,
        run: () => void logout(),
      },
    ],
    [navigate, setQuickAdd, setTheme, theme, logout],
  );

  function openHit(hit: SearchHit) {
    setOpen(false);
    if (hit.type === 'task' || hit.type === 'comment') navigate(`/tasks/${hit.key}`);
    else if (hit.type === 'project') navigate(`/projects/${hit.key}/board`);
    else if (hit.type === 'note') navigate(`/notes/${hit.id}`);
    else if (hit.type === 'user') navigate('/settings/members');
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh] backdrop-blur-[1px]"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <Command
        label="Command palette"
        shouldFilter={isCommandMode}
        className="w-[min(620px,calc(100vw-24px))] overflow-hidden rounded-[8px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elevation)]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3">
          <Search className="size-4 shrink-0 text-[var(--text-muted)]" />
          <Command.Input
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search, or type > for commands…"
            className="h-11 w-full bg-transparent text-[14px] outline-none placeholder:text-[var(--text-muted)]"
          />
        </div>

        <Command.List className="max-h-[380px] overflow-y-auto p-1.5">
          <Command.Empty className="px-3 py-6 text-center text-[13px] vt-muted">
            {term.length < 2 && !isCommandMode
              ? 'Type at least two characters, or > for commands.'
              : 'Nothing matches.'}
          </Command.Empty>

          {isCommandMode && (
            <Command.Group heading="Commands" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--text-muted)]">
              {commands.map((command) => (
                <Command.Item
                  key={command.id}
                  value={command.label}
                  onSelect={() => {
                    setOpen(false);
                    command.run();
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-2 text-[13px] data-[selected=true]:bg-[var(--surface-2)]"
                >
                  {command.icon}
                  <span className="flex-1">{command.label}</span>
                  {command.hint && (
                    <kbd className="vt-mono rounded-[4px] border border-[var(--border)] px-1 text-[10px] vt-muted">
                      {command.hint}
                    </kbd>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {!isCommandMode && results?.exact && (
            <Command.Group heading="Exact match" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--accent)]">
              <Command.Item
                value={results.exact.key ?? results.exact.id}
                onSelect={() => openHit(results.exact!)}
                className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-2 text-[13px] data-[selected=true]:bg-[var(--surface-2)]"
              >
                <span className="vt-mono text-[11px] text-[var(--accent)]">{results.exact.key}</span>
                <span className="truncate">{results.exact.title}</span>
              </Command.Item>
            </Command.Group>
          )}

          {!isCommandMode &&
            (results?.groups ?? []).map((group) => (
              <Command.Group
                key={group.type}
                heading={TYPE_LABEL[group.type]}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--text-muted)]"
              >
                {group.hits.map((hit) => (
                  <Command.Item
                    key={`${hit.type}:${hit.id}:${hit.title}`}
                    value={`${hit.key ?? ''} ${hit.title}`}
                    onSelect={() => openHit(hit)}
                    className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-2 text-[13px] data-[selected=true]:bg-[var(--surface-2)]"
                  >
                    {hit.key && <span className="vt-mono shrink-0 text-[11px] vt-muted">{hit.key}</span>}
                    <span className="min-w-0 flex-1 truncate">{hit.title}</span>
                    {hit.subtitle && (
                      <span className="shrink-0 text-[11px] vt-muted">{hit.subtitle}</span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}

          {!isCommandMode && term.length < 2 && (recents?.length ?? 0) > 0 && (
            <Command.Group heading="Recent" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--text-muted)]">
              {(recents ?? []).map((hit) => (
                <Command.Item
                  key={hit.id}
                  value={`${hit.key} ${hit.title}`}
                  onSelect={() => openHit(hit)}
                  className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-2 text-[13px] data-[selected=true]:bg-[var(--surface-2)]"
                >
                  <span className="vt-mono shrink-0 text-[11px] vt-muted">{hit.key}</span>
                  <span className="min-w-0 flex-1 truncate">{hit.title}</span>
                  <span className="shrink-0 text-[11px] vt-muted">{hit.subtitle}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>

        <footer className="flex items-center gap-3 border-t border-[var(--border)] px-3 py-1.5 text-[11px] vt-muted">
          <span>
            <kbd className="vt-mono">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="vt-mono">↵</kbd> open
          </span>
          <span>
            <kbd className="vt-mono">&gt;</kbd> commands
          </span>
          <span className="ml-auto">{user?.name}</span>
        </footer>
      </Command>
    </div>
  );
}
